import { customSanitize } from "@/utils/customSanitize.ts";
import { optimizeURL } from "@/utils/url.ts";
import { Date as DateComponent } from "@aleksundshantu/aunds-utils-react/components";
import styles from "./PostCard.module.scss";
import { ImageType } from "@/types/baseTypes.ts";
import { WP_Term } from "wp-types";
import { ContentTag } from "../ContentTag/ContentTag";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { MediaFormatType } from "@/types/ModuleOptionsType";
import { MediaItemResponsive } from "../MediaItem/MediaItemResponsive";
import { CustomLink } from "../CustomLink";
import { CategoryOverviewPages } from "@/getData/getIndexedPostGridData";
import { SVGIcon } from "../SVGIcon/SVGIcon";

export type PostsGridElementType = {
  id: number;
  date: string;
  title: string;
  link: string;
  image: ImageType | false;
  excerpt: string;
  categories: WP_Term[];
  media_format?: MediaFormatType;
  location?: string[];
};

interface PostCardProps {
  element: PostsGridElementType;
  readMoreLabel?: string;
  border_radius: boolean;
  box_shadow: boolean;
  showBox: boolean;
  grid_columns: "two-cols" | "three-cols";
  headlineLevel?: 3 | 4;
  media_format?: MediaFormatType;
  skeleton?: boolean;
  allCategories?: CategoryOverviewPages[];
  isNobleElements?: boolean;
}

const PostCard = ({
  element,
  border_radius,
  box_shadow,
  showBox,
  grid_columns = "three-cols",
  media_format,
  skeleton,
  headlineLevel = 3,
  isNobleElements = false,
}: PostCardProps) => {
  const { categories, excerpt, image, link, title, location = [] } = element;
  const { getLabel, getIcon } = useGlobalContext();
  const _filteredCategories = categories.filter(
    (el) => el.name.toLowerCase() !== "pressemitteilung",
  );

  // Todo: Remove this when postsslider have media_format set
  const defaultMediaFormat: MediaFormatType = {
    mobile: "square",
    tablet: "square",
    desktop: "square",
    fullhd: "square",
  };

  const skeletonElementClass = skeleton ? "skeleton-element" : "";

  const cardIsNE = !location.includes("noble-bc");
  const HeadingTag = headlineLevel === 3 ? "h3" : "h4";

  const resolvedLink = (() => {
    if (!link) return link;
    try {
      const url = new URL(link);
      const base = cardIsNE
        ? (process.env.NEXT_PUBLIC_PUBLIC_URL_ELEMENTS ?? url.origin)
        : (process.env.NEXT_PUBLIC_PUBLIC_URL ?? url.origin);
      return base + url.pathname + url.search + url.hash;
    } catch {
      return link;
    }
  })();

  // Cross-domain: post belongs to a different app than the current one.
  // CustomLink calls optimizeURL internally which strips the domain — use a plain <a> instead.
  const isCrossDomain = isNobleElements !== cardIsNE;
  const optimizedURL = isCrossDomain
    ? resolvedLink
    : optimizeURL(resolvedLink, isNobleElements);

  return (
    <article
      data-invert={cardIsNE}
      className={`${styles["post-grid-element"]} ${styles[grid_columns]} ${box_shadow && showBox ? "has-box-shadow" : ""} ${border_radius && showBox ? "has-border-radius" : ""} ${styles[image ? "has-image" : "no-image"]}`}
    >
      {resolvedLink && (
        <>
          {isCrossDomain ? (
            // eslint-disable-next-line react/forbid-elements
            <a
              href={resolvedLink}
              aria-label={getLabel("aria_label_postlink").replace(
                "$posttitle",
                title,
              )}
              className={styles["custom-link-overlay"]}
            >
              <span className={styles["only-sr"]}>{element.title}</span>
            </a>
          ) : (
            <CustomLink
              href={resolvedLink}
              target={"_self"}
              aria-label={getLabel("aria_label_postlink").replace(
                "$posttitle",
                title,
              )}
              disablePrefetch // Disable prefetching for grid links https://trello.com/c/g5k075FS
              className={styles["custom-link-overlay"]}
            >
              <span className={styles["only-sr"]}>{element.title}</span>
            </CustomLink>
          )}
          <span className={`${styles["link-btn"]}`}>
            <SVGIcon src={getIcon("ArrowBigRight")} width={14} height={14} />
          </span>
        </>
      )}
      <header className={styles["post-card-header"]}>
        {image && (
          <div
            className={`${styles["image-wrapper"]} ${resolvedLink ? styles["inverted-radius"] : ""}`}
          >
            {skeleton ? (
              <div className={`skeleton-element ${styles["skeleton-image"]}`} />
            ) : (
              <MediaItemResponsive
                hasOverlay={false}
                image_desktop={image}
                mediaFormat={media_format ? media_format : defaultMediaFormat}
                sizes="(max-width: 767px) 50vw, (max-width: 1023px) 100vw, (max-width: 1407px) 33vw, 512px"
                link={
                  optimizedURL
                    ? { url: optimizedURL, target: "_self", title: "" }
                    : undefined
                }
                linkProps={{ tabIndex: -1 }}
                disablePrefetch // Disable prefetching for grid links https://trello.com/c/g5k075FS
              />
            )}
          </div>
        )}

        <div
          className={`${styles["content-wrapper"]} ${showBox ? "is-box" : ""}`}
        >
          <div
            className={`${styles["categorie-date"]} ${skeletonElementClass}`}
          >
            {!isNobleElements &&
              _filteredCategories.map((cat) => (
                <span key={cat.slug}>
                  <CustomLink
                    className={styles["categorie"]}
                    href={
                      (cat as WP_Term & { link?: string }).link ??
                      `${process.env.NEXT_PUBLIC_PUBLIC_URL}/${cat.slug}`
                    }
                    target={"_self"}
                  >
                    {cat.name}
                  </CustomLink>
                  {" | "}
                </span>
              ))}

            <time dateTime={new Date(element.date).toISOString().split("T")[0]}>
              <DateComponent
                date={new Date(element.date)}
                dateFormat={"dd. MMM yyyy"}
              />
            </time>
          </div>

          {title && (
            <HeadingTag
              className={`${styles.title} title ${skeletonElementClass}`}
              dangerouslySetInnerHTML={{
                __html: customSanitize(title),
              }}
            />
          )}

          {excerpt && (
            <ContentTag
              content={excerpt}
              extraClass={`pb-0 mb-4 ${skeletonElementClass}`}
            />
          )}
        </div>
      </header>
    </article>
  );
};

export default PostCard;
